/**
 * Replay Player view — Faz 8.1 (gate criterion: deterministic playback).
 *
 * The user picks `[fromTickId, toTickId]`, hits Run, and the server
 * returns an array of per-step `ReplaySnapshot`s. Step-controls
 * (◀ ▶ scrubber) navigate through the snapshots; for each step we
 * render a `StateDiff` against the previous one, so investigators
 * see exactly what each event mutated. The `finalDigest` is shown
 * in the header — pasting the same range twice MUST yield the same
 * digest (server-side LRU + kernel determinism guarantee).
 */

import { useMemo, useState, type ReactElement } from "react";
import { useMutation } from "@tanstack/react-query";

import { ApiError } from "../api/client.js";
import { fetchReplay } from "../api/queries.js";
import type { ReplayResultWire, ReplayStateViewWire } from "../api/types.js";
import { StateDiff } from "../components/StateDiff.js";
import { JsonTree } from "../components/JsonTree.js";

export const ReplayPlayerView = (): ReactElement => {
  const [fromTickId, setFromTickId] = useState<string>("");
  const [toTickId, setToTickId] = useState<string>("");
  const [step, setStep] = useState<number>(0);

  const mutation = useMutation<ReplayResultWire, Error, { from: string; to: string }>({
    mutationFn: ({ from, to }) => fetchReplay(from, to),
    onSuccess: () => setStep(0),
  });

  const result = mutation.data;
  const snapshots = result?.snapshots ?? [];
  const current = snapshots[step] ?? null;
  const previous: ReplayStateViewWire | null = step > 0 ? snapshots[step - 1]?.stateView ?? null : null;
  const changedDigestSlices = useMemo(() => {
    if (current === null) return [];
    const previousSlices = new Map(
      step > 0 ? snapshots[step - 1]?.digestManifest.slices ?? [] : [],
    );
    return current.digestManifest.slices.filter(
      ([key, sliceDigest]) => previousSlices.get(key) !== sliceDigest,
    );
  }, [current, snapshots, step]);

  const onRun = (e: React.FormEvent): void => {
    e.preventDefault();
    if (fromTickId === "" || toTickId === "") return;
    mutation.mutate({ from: fromTickId, to: toTickId });
  };

  const totalSteps = snapshots.length;

  const stepNav = useMemo(() => {
    return {
      canPrev: step > 0,
      canNext: step + 1 < totalSteps,
      canFirst: step !== 0 && totalSteps > 0,
      canLast: step !== totalSteps - 1 && totalSteps > 0,
    };
  }, [step, totalSteps]);

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold">Replay Player</h2>
        <p className="text-sm text-slate-400">
          Re-execute archived events through the kernel reducer pipeline. Identical
          ranges always yield identical digests (deterministic gate).
        </p>
      </header>

      <form onSubmit={onRun} className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-slate-400">
          fromTickId
          <input
            value={fromTickId}
            onChange={(e) => setFromTickId(e.target.value)}
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm font-mono"
            placeholder="e.g. 1000"
          />
        </label>
        <label className="flex flex-col text-xs text-slate-400">
          toTickId
          <input
            value={toTickId}
            onChange={(e) => setToTickId(e.target.value)}
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm font-mono"
            placeholder="e.g. 1100"
          />
        </label>
        <button
          type="submit"
          disabled={mutation.isPending || fromTickId === "" || toTickId === ""}
          className="rounded bg-sky-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-40"
        >
          {mutation.isPending ? "Running…" : "Run replay"}
        </button>
      </form>

      {mutation.isError && (
        <p className="text-rose-400" data-testid="error">
          {mutation.error instanceof ApiError
            ? `${mutation.error.server.kind}: ${mutation.error.server.message}`
            : mutation.error.message}
        </p>
      )}

      {result && (
        <>
          <div className="rounded border border-slate-700 bg-slate-900/60 p-3 text-sm">
            <div>
              Range: <span className="font-mono">{result.fromTickId}</span> →{" "}
              <span className="font-mono">{result.toTickId}</span>
            </div>
            <div>
              Events: <span className="font-mono">{result.eventCount}</span>
            </div>
            <div>
              Final digest:{" "}
              <span data-testid="final-digest" className="font-mono text-xs text-emerald-400">
                {result.finalDigest}
              </span>
            </div>
          </div>

          {totalSteps > 0 && current && (
            <>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setStep(0)}
                  disabled={!stepNav.canFirst}
                  className="rounded border border-slate-700 px-2 py-1 text-xs disabled:opacity-30"
                >
                  ⏮
                </button>
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={!stepNav.canPrev}
                  className="rounded border border-slate-700 px-2 py-1 text-xs disabled:opacity-30"
                >
                  ◀
                </button>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, totalSteps - 1)}
                  value={step}
                  onChange={(e) => setStep(Number(e.target.value))}
                  className="flex-1"
                  aria-label="step scrubber"
                />
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
                  disabled={!stepNav.canNext}
                  className="rounded border border-slate-700 px-2 py-1 text-xs disabled:opacity-30"
                >
                  ▶
                </button>
                <button
                  type="button"
                  onClick={() => setStep(totalSteps - 1)}
                  disabled={!stepNav.canLast}
                  className="rounded border border-slate-700 px-2 py-1 text-xs disabled:opacity-30"
                >
                  ⏭
                </button>
                <span className="ml-2 text-xs text-slate-400">
                  Step {step + 1} / {totalSteps}
                </span>
              </div>

              <div className="rounded border border-slate-700 bg-slate-900/40 p-3 text-sm">
                <div>
                  tickId:{" "}
                  <span data-testid="step-tick" className="font-mono">
                    {current.tickId}
                  </span>{" "}
                  · kind: <span className="font-mono">{current.eventKind}</span>
                </div>
                <div className="text-xs text-slate-400">digest: {current.digest}</div>
              </div>

              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-400">
                  State diff vs. previous step
                </h3>
                <StateDiff prev={previous} next={current.stateView} />
              </section>

              <details open data-testid="digest-manifest-diff">
                <summary className="cursor-pointer text-sm text-slate-400">
                  Decision-state digest slices ({changedDigestSlices.length} changed)
                </summary>
                <ul className="mt-2 space-y-1 text-xs font-mono">
                  {changedDigestSlices.map(([key, sliceDigest]) => (
                    <li key={key}>
                      <span className="text-amber-400">~ {key}</span>{" "}
                      <span className="text-slate-400">{sliceDigest}</span>
                    </li>
                  ))}
                </ul>
              </details>

              <details>
                <summary className="cursor-pointer text-sm text-slate-400">
                  Raw state-view (current step)
                </summary>
                <JsonTree value={current.stateView} className="mt-2" />
              </details>
            </>
          )}
        </>
      )}
    </section>
  );
};
